
import { Union } from "../../fable_modules/fable-library-js.5.3.0/Types.js";
import { union_type, string_type } from "../../fable_modules/fable-library-js.5.3.0/Reflection.js";
import { margin_ml2, margin_ml1, justifyContent_justifyBetween, fontSize_textXs, backgroundColor_bgNeutral100, gap_gap$002D1, alignItems_itemsCenter, display_flex, display_grid, fontWeight_fontMedium, fontSize_textSm, padding_px3, padding_py2, boxShadow_shadowXs, borderRadius_roundedMd, width_wFull, display_block, margin_mt1, tw } from "../../fable_modules/Feliz.Tailwind.4.0.2/Strings.fs.js";
import { empty as empty_1, singleton, append, delay, toList } from "../../fable_modules/fable-library-js.5.3.0/Seq.js";
import { animation_animateFadeOut, textColor_textSuccess600, textColor_textSecondary800, hover, textColor_textSecondary600, textColor_textNeutral600, textColor_textDanger600, textColor_textNeutral400, textColor_textNeutral500, dark, gap_gap4, md, textColor_textNeutral700, borderColor_borderNeutral300, borderColor_borderDanger500, sm, FocusVisible_ringInset, backgroundColor_bgSurfaceInset } from "../TailwindExtensions.js";
import { singleton as singleton_1, empty, append as append_1, ofArray } from "../../fable_modules/fable-library-js.5.3.0/List.js";
import { HtmlHelper_createElement } from "../../fable_modules/Feliz.3.3.3/Html.fs.js";
import { useEffect, useRef, useState, createElement } from "react";
import { IconWeight, IconSize, IconName, icon } from "../Icons.js";
import { join, concat } from "../../fable_modules/fable-library-js.5.3.0/String.js";
import { defaultOf } from "../../fable_modules/fable-library-js.5.3.0/Util.js";
import { startImmediate } from "../../fable_modules/fable-library-js.5.3.0/Async.js";
import { singleton as singleton_2 } from "../../fable_modules/fable-library-js.5.3.0/AsyncBuilder.js";
import { ApiErrorModule_message } from "../ValidationErrors.js";
import { disposeSafe, equals } from "../../fable_modules/fable-library-js.5.3.0/Util.js";

export class FieldStatus extends Union {
    constructor(tag, fields) {
        super();
        this.tag = tag;
        this.fields = fields;
    }
    cases() {
        return ["Idle", "Saving", "Saved", "SaveError"];
    }
}

export function FieldStatus_$reflection() {
    return union_type("Shared.AutoSave.FieldStatus", [], FieldStatus, () => [[], [], [], [["Item", string_type]]]);
}

/**
 * Shared input field styling with surface-inset background and focus-visible ring
 */
export function inputClass(hasError) {
    return tw(toList(delay(() => append(singleton(margin_mt1), delay(() => append(singleton(display_block), delay(() => append(singleton(width_wFull), delay(() => append(singleton(borderRadius_roundedMd), delay(() => append(singleton(boxShadow_shadowXs), delay(() => append(singleton(padding_py2), delay(() => append(singleton(padding_px3), delay(() => append(singleton(backgroundColor_bgSurfaceInset), delay(() => append(singleton(FocusVisible_ringInset), delay(() => append(singleton(sm(fontSize_textSm)), delay(() => (hasError ? singleton(borderColor_borderDanger500) : singleton(borderColor_borderNeutral300)))))))))))))))))))))))));
}

export const labelClass = tw(ofArray([display_block, fontSize_textSm, fontWeight_fontMedium, textColor_textNeutral700]));

/**
 * Responsive grid containing a date input and a time input side-by-side.
 * Used by both CreateChallengeView and ChallengeEditView for start date/time.
 */
export function dateTimeInputGrid(dateId, dateValue, dateHasError, dateMinMax, dateProps, timeId, timeValue, timeHasError, timeStep, timeAriaLabel, timeProps) {
    return HtmlHelper_createElement("div", ofArray([["className", tw(ofArray([display_grid, "grid-cols-1", md("grid-cols-2"), gap_gap4, margin_mt1]))], ["children", [HtmlHelper_createElement("input", append_1(ofArray([["id", dateId], ["type", "date"], ["className", inputClass(dateHasError)], ["value", dateValue]]), append_1((dateMinMax == null) ? empty() : ofArray([["min", dateMinMax[0]], ["max", dateMinMax[1]]]), dateProps))), HtmlHelper_createElement("input", append_1(ofArray([["id", timeId], ["type", "time"], ["step", timeStep], ["aria-label", timeAriaLabel], ["className", inputClass(timeHasError)], ["value", timeValue]]), timeProps))]]]));
}

/**
 * Read-only field with Lock icon showing why editing is disabled
 */
export function lockedField(label, value, reason) {
    return HtmlHelper_createElement("div", singleton_1(["children", [HtmlHelper_createElement("div", ofArray([["className", tw(ofArray([display_flex, alignItems_itemsCenter, gap_gap$002D1, fontSize_textSm, fontWeight_fontMedium, textColor_textNeutral700]))], ["children", [createElement(icon, {
        name: new IconName(33, []),
        size: new IconSize(0, []),
        weight: new IconWeight(1, []),
    }), HtmlHelper_createElement("span", singleton_1(["children", singleton_1(label)]))]]])), HtmlHelper_createElement("div", ofArray([["className", tw(ofArray([margin_mt1, padding_py2, padding_px3, backgroundColor_bgNeutral100, dark("bg-neutral-800"), borderRadius_roundedMd, textColor_textNeutral500, fontSize_textSm]))], ["children", singleton_1(value)]])), HtmlHelper_createElement("p", ofArray([["className", tw(ofArray([margin_mt1, fontSize_textXs, textColor_textNeutral400]))], ["children", singleton_1(reason)]]))]]));
}

/**
 * Wrapper for a form field with consistent label, control, error/helper text, and ARIA wiring.
 * label: display label text
 * htmlFor: id of the input control (for label association)
 * error: field error message (string option)
 * helperText: helper/hint text shown when no error (string option)
 * required: whether the field is required
 * statusIndicator: optional status element (e.g., "Saved", "Saving...")
 * buildChild: render function receiving computed ARIA props to apply to the form control
 */
export function formField(label, htmlFor, error, helperText, required, statusIndicator, buildChild) {
    const errorId = concat(htmlFor, "-error");
    const hasError = error != null;
    const ariaProps = toList(delay(() => append(required ? singleton(["aria-required", "true"]) : empty_1(), delay(() => (hasError ? append(singleton(["aria-invalid", "true"]), delay(() => singleton(["aria-describedby", errorId]))) : empty_1())))));
    return HtmlHelper_createElement("div", singleton_1(["children", toList(delay(() => append(singleton(HtmlHelper_createElement("div", ofArray([["className", tw(ofArray([display_flex, alignItems_itemsCenter, justifyContent_justifyBetween]))], ["children", toList(delay(() => append(singleton(HtmlHelper_createElement("label", ofArray([["htmlFor", htmlFor], ["className", labelClass], ["children", toList(delay(() => append(singleton(label), delay(() => (required ? singleton(HtmlHelper_createElement("span", ofArray([["aria-hidden", "true"], ["className", tw(ofArray([textColor_textDanger600, margin_ml1]))], ["children", singleton_1("*")]]))) : empty_1())))))]]))), delay(() => {
        const matchValue = statusIndicator;
        return (matchValue == null) ? singleton(defaultOf()) : singleton(matchValue);
    }))))]]))), delay(() => append(singleton(buildChild(ariaProps)), delay(() => {
        const matchValue_1 = error;
        if (matchValue_1 == null) {
            const matchValue_2 = helperText;
            if (matchValue_2 == null) {
                return singleton(defaultOf());
            }
            else {
                const helper = matchValue_2;
                return singleton(HtmlHelper_createElement("p", ofArray([["className", tw(ofArray([margin_mt1, fontSize_textSm, textColor_textNeutral600]))], ["children", singleton_1(helper)]])));
            }
        }
        else {
            const errMsg = matchValue_1;
            return singleton(HtmlHelper_createElement("div", ofArray([["id", errorId], ["role", join(" ", ["alert"])], ["className", tw(ofArray([margin_mt1, display_flex, alignItems_itemsCenter, gap_gap$002D1, fontSize_textSm, textColor_textDanger600]))], ["children", [createElement(icon, {
                name: new IconName(7, []),
                size: new IconSize(0, []),
                weight: new IconWeight(1, []),
            }), HtmlHelper_createElement("span", singleton_1(["children", singleton_1(errMsg)]))]]])));
        }
    }))))))]));
}

export const autoSaveDelayMs = 1500;

/**
 * Execute a save operation, updating status through the lifecycle
 */
export function executeSave(setStatus, apiCall) {
    setStatus(new FieldStatus(1, []));
    startImmediate(singleton_2.Delay(() => singleton_2.TryWith(singleton_2.Delay(() => singleton_2.Bind(apiCall(), (_arg) => {
        const result = _arg;
        if (result.tag === 1) {
            setStatus(new FieldStatus(3, [ApiErrorModule_message(result.fields[0])]));
            return singleton_2.Zero();
        }
        else {
            setStatus(new FieldStatus(2, []));
            return singleton_2.Zero();
        }
    })), (_arg_1) => {
        setStatus(new FieldStatus(3, ["An unexpected error occurred"]));
        return singleton_2.Zero();
    })));
}

/**
 * Auto-save hook: debounces field changes and saves after a delay.
 * `key` must be a value-equal string derived from the field state — the effect dependency is
 * compared by reference, so an allocated value (tuple, list, record) re-arms the debounce on
 * every render and turns it into an endless save loop.
 * `hasChanged` compares the field against the value the page loaded with; once this hook has
 * saved, the comparison is against the last saved key instead, so returning to the loaded value
 * after a save is still persisted.
 * Returns (status, retryFn) for display and error recovery.
 */
export function useAutoSave(key, hasChanged, hasError, performSave, delayMs) {
    const patternInput = useState(new FieldStatus(0, []));
    const status = patternInput[0];
    const setStatus = patternInput[1];
    const saveRef = useRef(performSave);
    saveRef.current = performSave;
    const timerRef = useRef(0);
    const savedKeyRef = useRef(undefined);
    let isUnsaved;
    const matchValue = savedKeyRef.current;
    isUnsaved = ((matchValue == null) ? hasChanged : (key !== matchValue));
    const save = (savingKey) => {
        saveRef.current((newStatus) => {
            if (equals(newStatus, new FieldStatus(2, []))) {
                savedKeyRef.current = savingKey;
            }
            setStatus(newStatus);
        });
    };
    const dependencies = [key, hasError];
    useEffect(() => {
        const setup = (() => {
        let disp;
        clearTimeout(timerRef.current);
        if (isUnsaved && !hasError) {
            timerRef.current = setTimeout(() => {
                save(key);
            }, delayMs);
        }
        else if (!isUnsaved) {
            setStatus(new FieldStatus(0, []));
        }
        disp = {
            Dispose() {
                clearTimeout(timerRef.current);
            },
        };
        return () => {
            let copyOfStruct = disp;
            disposeSafe(copyOfStruct);
        };
    })();
        return setup;
        }, dependencies);;
    const dependencies_2 = [status];
    useEffect(() => {
        const setup = (() => {
        let disp_1;
        if (equals(status, new FieldStatus(2, []))) {
            const timerId = setTimeout(() => {
                setStatus(new FieldStatus(0, []));
            }, 2000) | 0;
            disp_1 = {
                Dispose() {
                    clearTimeout(timerId);
                },
            };
        }
        else {
            disp_1 = {
                Dispose() {
                },
            };
        }
        return () => {
            let copyOfStruct_1 = disp_1;
            disposeSafe(copyOfStruct_1);
        };
    })();
        return setup;
        }, dependencies_2);;
    return [status, () => {
        if (isUnsaved) {
            save(key);
        }
    }];
}

/**
 * Status indicator for auto-saved fields
 */
export function fieldStatusIndicator(status, onRetry) {
    switch (status.tag) {
        case 3:
            return HtmlHelper_createElement("div", singleton_1(["children", [HtmlHelper_createElement("span", ofArray([["className", tw(ofArray([fontSize_textSm, textColor_textDanger600]))], ["children", singleton_1(status.fields[0])]])), HtmlHelper_createElement("button", ofArray([["className", tw(ofArray([margin_ml2, fontSize_textSm, textColor_textSecondary600, hover(textColor_textSecondary800)]))], ["children", singleton_1("Retry")], ["onClick", (_arg) => {
                onRetry();
            }]]))]]));
        case 1:
            return HtmlHelper_createElement("span", ofArray([["className", tw(ofArray([fontSize_textSm, textColor_textNeutral500]))], ["children", singleton_1("Saving...")]]));
        case 0:
            return defaultOf();
        default:
            return HtmlHelper_createElement("span", ofArray([["className", tw(ofArray([fontSize_textSm, textColor_textSuccess600, animation_animateFadeOut]))], ["children", singleton_1("Saved")]]));
    }
}

//# sourceMappingURL=AutoSave.js.map
